/**
 * Menu config types (migration 131; SPEC §9.1). Mirror the DTOs in
 * api/Functions/MenuFunctions.cs and api/Functions/MenuAdminFunctions.cs exactly.
 */

/** Menu = category, Submenu = sub-category, Route = in-app page, Report = external link. */
export type MenuKind = 'Menu' | 'Submenu' | 'Report' | 'Route';

/**
 * Leaves carry a Url, have no children and can never be a parent.
 *
 * ⛔ KEEP IN STEP with `MenuValidation.LeafKinds` in api/MenuValidation.cs —
 * `MenuConfigTests.The_client_and_server_agree_on_the_leaf_kinds` reads this
 * line. If the two drift, a kind one side treats as a leaf and the other walks
 * as a category vanishes from the sidebar with no error.
 */
export const LEAF_KINDS: readonly MenuKind[] = ['Report', 'Route'];

export function isLeafKind(kind: string | null | undefined): boolean {
  return kind != null && (LEAF_KINDS as readonly string[]).includes(kind);
}

/** Mirrors `MenuValidation.IsInternalAppPath` and CK_MenuItem_RouteUrl. */
export function isInternalAppPath(url: string | null | undefined): boolean {
  const u = url?.trim() ?? '';
  return u.startsWith('/') && !u.startsWith('//') && !u.includes('://');
}

/** Plain-English names for the kinds, for the editor. */
export const KIND_LABEL: Record<MenuKind, string> = {
  Menu: 'Category',
  Submenu: 'Sub-category',
  Route: 'Page',
  Report: 'Link',
};

/** A node of GET /api/menu — the caller's role-pruned tree. */
export interface MenuNode {
  MenuItemId: number;
  Kind: MenuKind;
  Title: string;
  Description: string | null;
  Icon: string | null;
  Url: string | null;
  Target: '_self' | '_blank';
  Children: MenuNode[];
}

/** A flat row of GET /api/menu/admin. */
export interface AdminMenuItem {
  MenuItemId: number;
  ParentId: number | null;
  Kind: MenuKind;
  Title: string;
  Description: string | null;
  Icon: string | null;
  Url: string | null;
  Target: '_self' | '_blank';
  SortOrder: number;
  IsActive: boolean;
  RowVersion: string;
  Roles: string[];
}

/** GET /api/menu/roles. */
export interface MenuRole {
  RoleName: string;
  DisplayName: string;
}

export interface MenuItemWrite {
  ParentId?: number | null;
  Kind: MenuKind;
  Title: string;
  Description: string | null;
  Icon: string | null;
  Url: string | null;
  Target: '_self' | '_blank';
  IsActive: boolean;
  Roles?: string[];
  RowVersion?: string;
}

export interface MenuReorderEntry {
  MenuItemId: number;
  ParentId: number | null;
  SortOrder: number;
}
